import { useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useTelemetryConsent } from '@/ui/state/telemetryConsentContext'
import {
  plausibleEventExample,
  sentryErrorExample,
  sentryPerformanceExample,
} from '@/infrastructure/sentry/promptExamples'

/**
 * First-upload observability consent prompt (PRD US 94, ADR-035, S35).
 *
 * Visible iff the consent store is loaded, consent is `false`, the user
 * has never engaged with the consent system before (`!dismissed`), and
 * the user is on a project route — a coarse proxy for "the user has
 * actually used the tool once." Dismissing or accepting sets
 * `dismissedAt`; the prompt never reappears (ADR-035).
 *
 * The expandable section shows the literal JSON of three example
 * envelopes; their integrity vs. the production scrubbers is asserted
 * by `promptExamples.test.ts` (S37 prompt-parity).
 */
export function ConsentPrompt() {
  const { loaded, consenting, dismissed, setConsent, dismissPrompt } =
    useTelemetryConsent()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const [showExamples, setShowExamples] = useState(false)

  const shouldShow =
    loaded && !consenting && !dismissed && pathname.startsWith('/p/')

  if (!shouldShow) return null

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4"
      role="region"
      aria-label={t('consentPrompt.title')}
    >
      <div
        className="mx-auto max-w-3xl rounded-lg border p-4 sm:p-6 flex flex-col gap-3 shadow-lg"
        style={{
          background: 'var(--color-bg)',
          borderColor: 'var(--color-muted)',
        }}
      >
        <h2
          className="text-lg font-medium"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('consentPrompt.title')}
        </h2>
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('consentPrompt.body')}
        </p>
        <button
          type="button"
          className="self-start text-xs underline"
          style={{ color: 'var(--color-muted)' }}
          onClick={() => setShowExamples((v) => !v)}
          aria-expanded={showExamples}
        >
          {showExamples
            ? t('consentPrompt.hideExamples')
            : t('consentPrompt.showExamples')}
        </button>
        {showExamples && (
          <div className="flex flex-col gap-2 text-xs">
            <ExamplePane
              label={t('consentPrompt.examplePlausible')}
              payload={plausibleEventExample}
            />
            <ExamplePane
              label={t('consentPrompt.exampleSentryError')}
              payload={sentryErrorExample}
            />
            <ExamplePane
              label={t('consentPrompt.exampleSentryPerf')}
              payload={sentryPerformanceExample}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <button
            type="button"
            className="rounded px-3 py-1 text-sm"
            style={{
              background: 'var(--color-down)',
              color: 'var(--color-accent-fg)',
            }}
            onClick={() => {
              void setConsent(true)
            }}
          >
            {t('consentPrompt.accept')}
          </button>
          <button
            type="button"
            className="text-sm underline"
            style={{ color: 'var(--color-muted)' }}
            onClick={() => {
              void dismissPrompt()
            }}
          >
            {t('consentPrompt.decline')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ExamplePane({
  label,
  payload,
}: {
  label: string
  payload: unknown
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="font-medium" style={{ color: 'var(--color-fg)' }}>
        {label}
      </div>
      <pre
        className="overflow-auto rounded p-2 text-xs"
        style={{
          background: 'color-mix(in srgb, var(--color-fg) 5%, transparent)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  )
}
